import { ControlService, ToggleVal, controlServiceImpl } from "@lvt/streaming";
import {
    Box,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Switch,
    CircularProgress,
} from "@material-ui/core";
import { Alert as ToastAlert } from "@material-ui/lab";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import client from "@/api/client";
import { RelayAction } from "@/api/streamMetadata";
import { RootState, useSelector } from "@/store";
import { Severity } from "@/store/alerts/slice";
import * as FullStory from "@/utilities/hooks/fullstory";


const DEFAULT_RELAY_DELAY = 60_000;

interface LightsButtonItemProps {
    light: RelayAction;
    disableGutters?: boolean;
}

const LightsButtonItem: React.FC<LightsButtonItemProps> = ({ light, disableGutters }) => {
    const { t } = useTranslation("translation", { keyPrefix: "components.liveUnitMosaicPage" });

    const [on, setOn] = useState<boolean>(light.stateDefault ?? false);
    const [attempting, setAttempting] = useState(false);
    const toggleTimeout = useRef<null | ReturnType<typeof setTimeout>>(null);
    const [controlService, setControlService] = useState<ControlService>();

    const cameraId = useSelector((state: RootState) => state.cameras.streamingId);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    useEffect(() => {
        if (cameraId) {
            // single camera stream
            setControlService(controlServiceImpl(client, cameraId));
        } else if (light.cameraId) {
            // mosaic stream
            setControlService(controlServiceImpl(client, light.cameraId));
        }
    }, [cameraId, light]);

    useEffect(() => {
        // remove timeout on dismount
        return () => {
            if (toggleTimeout.current) {
                window.clearTimeout(toggleTimeout.current);
                toggleTimeout.current = null;
            }
        };
    }, []);

    const sendCommand = async (isChecked: boolean, service: ControlService) => {
        const toggle = isChecked ? ToggleVal.On : ToggleVal.Off;

        light.relayName !== "camera_light"
            ? await service.relay(toggle, light.relayName)
            : await service.cameraLight(toggle);
    };

    const onToggleFail = (error: string) => {
        const alert = (
            <ToastAlert key="light-alert" severity="error">
                {t("errors.toggleFailed", { message: error })}
            </ToastAlert>
        );

        const key = enqueueSnackbar(alert, {
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
            },
            autoHideDuration: 8_000,
            key: "light-alert",
            onClick: () => closeSnackbar(key),
            preventDuplicate: true,
            variant: Severity.Error,
        });
    };

    const onToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;

        if (controlService) {
            setAttempting(true);

            sendCommand(isChecked, controlService).then(() => {
                setOn(isChecked);

                // if toggling the light on, set a delayed toggle off
                if (light.stateDefault !== isChecked) {
                    toggleTimeout.current = setTimeout(() => {
                        sendCommand(isChecked, controlService);

                        setOn(light.stateDefault ?? isChecked);
                        toggleTimeout.current = null;
                    }, light.delay ?? DEFAULT_RELAY_DELAY);
                }
            }).catch((error) => {
                onToggleFail(error.message);

                setOn(light.stateDefault ?? isChecked);
            }).finally(() => {
                setAttempting(false);
            });
        } else {
            console.error("Failed to set controlService");
        }
    };

    const fullStorySecondaryClassName = () => {
        switch (light.relayName) {
            case "strobe":
                return FullStory.names.classes.streaming.strobeLight;
            case "irlight":
                return FullStory.names.classes.streaming.floodLight;
            case "camera_light":
                return FullStory.names.classes.streaming.cameraLight;
            default:
                return "";
        }
    };

    return (
        <ListItem disableGutters={disableGutters} key={light.uiLabel} className={clsx(FullStory.names.classes.streaming.light, fullStorySecondaryClassName())}>
            <ListItemText primary={light.uiLabel} />
            <ListItemSecondaryAction>
                <Box display="flex" alignItems="center">
                    {attempting && <CircularProgress color="secondary" />}
                    <Switch
                        id={`streaming_lights-button_${light.relayName}-toggle`}
                        checked={on}
                        edge="end"
                        onChange={onToggle}
                        inputProps={{
                            "aria-label": light.uiLabel,
                        }}
                    />
                </Box>
            </ListItemSecondaryAction>
        </ListItem>
    );
};
export default LightsButtonItem;
